import { api } from '../client';
import type { Address } from '../../types';

// ==================== TRANSPORT ORDERS ====================

export interface TransportOrder {
  id: string;
  orderNumber: string;
  companyId: string;
  status: string; // DRAFT, PENDING_ROUTE, OPTIMIZED, BOOKED, CANCELLED
  shipmentType: string;

  // Origin & Destination
  originCity: string;
  originState: string;
  originCountry: string;
  originPostal?: string;
  destCity: string;
  destState: string;
  destCountry: string;
  destPostal?: string;

  // Cargo Details
  totalWeight: number;
  totalVolume: number;
  pieceCount: number;
  requiresHazmat: boolean;
  requiresTempControl: boolean;
  specialInstructions?: string;

  // Schedule
  pickupDate: string;
  deliveryDate: string;

  // Relations
  routeOptions?: RouteOption[];
  appointments?: Appointment[];

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface RouteOption {
  id: string;
  transportOrderId: string;
  optionNumber: number;
  totalCost: number;
  totalDistance: number;
  totalTransitDays: number;
  carbonEmissions?: number;
  isRecommended: boolean;
  costScore?: number;
  timeScore?: number;
  reliabilityScore?: number;
  legs?: RouteLeg[];
  createdAt: string;
}

export interface RouteLeg {
  id: string;
  routeOptionId: string;
  legNumber: number;
  mode: string;
  carrierId?: string;
  carrierName: string;
  originCity: string;
  originState: string;
  originCountry: string;
  destCity: string;
  destState: string;
  destCountry: string;
  distance: number;
  transitDays: number;
  cost: number;
  departureTime?: string;
  arrivalTime?: string;
}

export interface CreateTransportOrderDto {
  orderNumber?: string;
  shipmentType: string;
  originCity: string;
  originState: string;
  originCountry: string;
  originPostal?: string;
  destCity: string;
  destState: string;
  destCountry: string;
  destPostal?: string;
  totalWeight: number;
  totalVolume: number;
  pieceCount: number;
  requiresHazmat?: boolean;
  requiresTempControl?: boolean;
  specialInstructions?: string;
  pickupDate: string;
  deliveryDate: string;
}

export interface UpdateTransportOrderDto extends Partial<CreateTransportOrderDto> {
  status?: string;
}

export interface QueryTransportOrderParams {
  status?: string;
  shipmentType?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const transportOrdersApi = {
  async getAll(params?: QueryTransportOrderParams) {
    const response = await api.get<{
      data: TransportOrder[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/tms/transport-orders', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<TransportOrder>(`/tms/transport-orders/${id}`);
    return response.data;
  },

  async create(data: CreateTransportOrderDto) {
    const response = await api.post<TransportOrder>('/tms/transport-orders', data);
    return response.data;
  },

  async update(id: string, data: UpdateTransportOrderDto) {
    const response = await api.patch<TransportOrder>(`/tms/transport-orders/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/transport-orders/${id}`);
    return response.data;
  },

  async generateRoutes(id: string) {
    const response = await api.post<RouteOption[]>(`/tms/transport-orders/${id}/generate-routes`);
    return response.data;
  },

  async selectRoute(orderId: string, routeId: string) {
    const response = await api.post<TransportOrder>(
      `/tms/transport-orders/${orderId}/select-route`,
      { routeId }
    );
    return response.data;
  },
};

// ==================== APPOINTMENTS ====================

export interface Appointment {
  id: string;
  appointmentNumber: string;
  companyId: string;
  type: string; // PICKUP, DELIVERY
  transportOrderId?: string;
  shipmentId?: string;
  locationId?: string;
  locationName: string;
  address: Address;
  scheduledDate: string;
  scheduledTimeStart: string;
  scheduledTimeEnd: string;
  status: string; // SCHEDULED, CONFIRMED, IN_PROGRESS, COMPLETED, CANCELLED
  actualArrival?: string;
  actualDeparture?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateAppointmentDto {
  type: string;
  transportOrderId?: string;
  shipmentId?: string;
  locationId?: string;
  locationName: string;
  address: Address;
  scheduledDate: string;
  scheduledTimeStart: string;
  scheduledTimeEnd: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
}

export interface UpdateAppointmentDto extends Partial<CreateAppointmentDto> {
  status?: string;
  actualArrival?: string;
  actualDeparture?: string;
}

export interface QueryAppointmentParams {
  start: string;
  end: string;
  type?: string;
  status?: string;
  locationId?: string;
}

// ==================== LOADS ====================

/**
 * Shipment available for load building
 */
export interface AvailableShipment {
  id: string;
  shipmentNumber: string;
  originCity: string;
  destinationCity: string;
  weight: number;
  volume: number;
  pallets?: number;
  pickupDate?: string;
  deliveryDate?: string;
  status: string;
}

export interface Load {
  id: string;
  loadNumber: string;
  companyId: string;
  carrierId?: string;
  equipmentType: string;
  maxWeight: number;
  maxVolume: number;
  maxPallets?: number;
  currentWeight: number;
  currentVolume: number;
  currentPallets: number;
  utilizationPct: number;
  status: string; // PLANNING, OPTIMIZED, ASSIGNED, LOADING, IN_TRANSIT, DELIVERED
  pickupDate?: string;
  deliveryDate?: string;
  originHub?: string;
  destinationHub?: string;
  shipmentLoads?: ShipmentLoad[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ShipmentLoad {
  id: string;
  loadId: string;
  shipmentId: string;
  sequence: number;
  weight: number;
  volume: number;
  pallets?: number;
  createdAt: string;
}

export interface CreateLoadDto {
  equipmentType: string;
  carrierId?: string;
  maxWeight: number;
  maxVolume: number;
  maxPallets?: number;
  pickupDate?: string;
  deliveryDate?: string;
  originHub?: string;
  destinationHub?: string;
}

export interface UpdateLoadDto extends Partial<CreateLoadDto> {
  status?: string;
}

export interface QueryLoadParams {
  status?: string;
  carrierId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// appointmentsApi and consolidationApi lived here, wrapping /tms/appointments
// and /tms/consolidations — 16 endpoints across two modules that do not exist
// on the server and never have. Nothing imported either object, so this was a
// client for an API that was never written. loadBuilderApi below is the real
// one; its six endpoints are implemented and in use.

export const loadBuilderApi = {
  async getAll(params?: QueryLoadParams) {
    const response = await api.get<{
      data: Load[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/tms/loads', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Load>(`/tms/loads/${id}`);
    return response.data;
  },

  async create(data: CreateLoadDto) {
    const response = await api.post<Load>('/tms/loads', data);
    return response.data;
  },

  async update(id: string, data: UpdateLoadDto) {
    const response = await api.patch<Load>(`/tms/loads/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/loads/${id}`);
    return response.data;
  },

  async getAvailableShipments(
    companyId: string,
    filters?: {
      originCity?: string;
      destCity?: string;
      maxWeight?: number;
      maxVolume?: number;
    }
  ) {
    const response = await api.get<AvailableShipment[]>('/tms/loads/available-shipments', {
      params: {
        companyId,
        ...filters,
      },
    });
    return response.data;
  },

  async addShipment(
    loadId: string,
    shipmentId: string,
    weight: number,
    volume: number,
    pallets?: number
  ) {
    const response = await api.post<Load>(`/tms/loads/${loadId}/shipments`, {
      shipmentId,
      weight,
      volume,
      pallets,
    });
    return response.data;
  },

  async removeShipment(loadId: string, shipmentId: string) {
    const response = await api.delete<Load>(`/tms/loads/${loadId}/shipments/${shipmentId}`);
    return response.data;
  },

  async optimize(loadId: string) {
    const response = await api.post<Load>(`/tms/loads/${loadId}/optimize`);
    return response.data;
  },
};

// ==================== CONSOLIDATIONS ====================

export interface Consolidation {
  id: string;
  consolidationNumber: string;
  companyId: string;
  status: string; // PLANNING, READY, SHIPPED, CANCELLED
  originHub: string;
  destinationHub: string;
  carrierId?: string;
  estimatedSavings?: number;
  targetShipDate: string;
  actualShipDate?: string;
  shipmentIds: string[];
  totalShipments: number;
  totalWeight: number;
  totalVolume: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateConsolidationDto {
  originHub: string;
  destinationHub: string;
  carrierId?: string;
  targetShipDate: string;
  shipmentIds?: string[];
}

export interface UpdateConsolidationDto extends Partial<CreateConsolidationDto> {
  status?: string;
  actualShipDate?: string;
}

export interface QueryConsolidationParams {
  status?: string;
  originHub?: string;
  destinationHub?: string;
  search?: string;
  page?: number;
  limit?: number;
}
